import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import type { OnyxConfirmModalData } from '@onyx/angular';
import {
  DropdownHelper,
  OnyxConfirmModalComponent,
  OnyxDropdownComponent,
  OnyxFormMode,
  OnyxModalService,
  OnyxSectionComponent,
  OnyxTableComponent,
  OnyxToggleComponent,
  UtilityHelper,
} from '@onyx/angular';
import { differenceBy } from 'lodash';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, tap } from 'rxjs';
import { SHORT_PAGINATION } from '../../../../../common/constants/common/short-pagination';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { FleetState } from '../../../../fleet/common/enums/fleet-state';
import { FleetHelper } from '../../../../fleet/common/helpers/fleet.helper';
import type {
  Fleet,
  SimplifiedFleet,
} from '../../../../fleet/common/interfaces/fleet';
import { BASE_ASSIGNED_VEHICLES_FORM_COLUMNS } from '../../common/constants/base-assigned-vehicles-form-columns';
import { BASE_ASSIGNED_VEHICLES_NOT_FOUND } from '../../common/constants/base-assigned-vehicles-not-found';

type BaseAssignedVehiclesFormArray = ReturnType<
  (typeof BaseAssignedVehiclesFormComponent)['buildForm']
>;
export type BaseAssignedVehiclesFormGroup = ReturnType<
  (typeof BaseAssignedVehiclesFormComponent)['buildAssignedVehicleForm']
>;

@Component({
  selector: 'app-base-assigned-vehicles-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxTableComponent,
    OnyxToggleComponent,
  ],
  templateUrl: './base-assigned-vehicles-form.component.html',
  styleUrl: './base-assigned-vehicles-form.component.scss',
})
export class BaseAssignedVehiclesFormComponent implements OnInit {
  private readonly modalService = inject(OnyxModalService);
  private readonly injector = inject(Injector);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly fleetHelper = inject(FleetHelper);

  public readonly form = input.required<BaseAssignedVehiclesFormArray>();
  public readonly mode = input(OnyxFormMode.ADD);

  protected readonly I18N = 'bases.assignedVehicles';
  protected readonly COLUMNS = BASE_ASSIGNED_VEHICLES_FORM_COLUMNS;
  protected readonly NOT_FOUND = BASE_ASSIGNED_VEHICLES_NOT_FOUND;
  protected readonly VEHICLES_SOURCE = this.fleetHelper.getVehiclesSource({
    filters: [FleetHelper.createStateFilter(FleetState.ACTIVE)],
  });

  protected readonly DictionaryCode = DictionaryCode;

  protected vehicles = signal<Fleet[] | null>(null);
  protected pagination = signal(SHORT_PAGINATION);
  protected totalVehiclesCount = signal<number | null>(null);

  protected assignAllVehicles = linkedSignal(
    () => this.vehicles()?.length === this.totalVehiclesCount(),
  );

  protected vehiclesList = computed(() =>
    CommonHelper.getPaginatedData(this.vehiclesControls(), this.pagination()),
  );

  protected get vehiclesControl() {
    return this.form().controls.vehicles;
  }

  private vehiclesControls!: Signal<typeof this.vehiclesControl.controls>;

  public ngOnInit(): void {
    this.vehiclesControls = toSignal(
      this.vehiclesControl.valueChanges.pipe(
        map(() => this.vehiclesControl.controls),
      ),
      { initialValue: this.vehiclesControl.controls, injector: this.injector },
    );

    this.form().controls.selectedVehicles.setValue(
      this.vehiclesControl.getRawValue().map((group) => group.uuid),
    );

    effect(
      () => {
        const selectedVehicles = this.vehicles();
        if (!selectedVehicles) return;

        const activeVehicles = this.vehiclesControl
          .getRawValue()
          .map((group) => group.vehicle);

        const removedVehicles = differenceBy(
          activeVehicles,
          selectedVehicles,
          'uuid',
        );
        for (const vehicle of removedVehicles) {
          const index = this.vehiclesControl
            .getRawValue()
            .findIndex((group) => group.uuid === vehicle.uuid);
          this.vehiclesControl.removeAt(index);
        }

        const addedVehicles = differenceBy(
          selectedVehicles,
          activeVehicles,
          'uuid',
        );
        for (const vehicle of addedVehicles) {
          this.vehiclesControl.push(
            BaseAssignedVehiclesFormComponent.buildAssignedVehicleForm(
              this.fb,
              vehicle,
            ),
          );
        }

        this.vehiclesControl.controls = [...this.vehiclesControl.controls];
      },
      { injector: this.injector },
    );
  }

  protected assign(): void {
    if (this.assignAllVehicles()) {
      setTimeout(() => {
        this.assignAllVehicles.set(false);
        this.modalService.open<OnyxConfirmModalData, boolean>(
          OnyxConfirmModalComponent,
          {
            heading: 'bases.confirmModal.vehicles.heading',
            message: 'bases.confirmModal.vehicles.description',
            confirmButtonText: 'labels.assign',
            action: () =>
              UtilityHelper.maybeAsyncToObservable(
                this.VEHICLES_SOURCE.list('', {
                  page: 1,
                  limit: Number.MAX_SAFE_INTEGER,
                }),
              ).pipe(
                tap(({ options }) => {
                  const vehicles =
                    DropdownHelper.mapToOptions(options)?.map(
                      (option) => option.value.uuid,
                    ) ?? [];
                  this.form().controls.selectedVehicles.setValue(vehicles);
                }),
              ),
          },
        );
      });
    }
  }

  protected openVehicleModal(data: BaseAssignedVehiclesFormGroup): void {
    const vehicle = data.controls.vehicle.getRawValue();
    this.fleetHelper.openModal(vehicle);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      selectedVehicles: fb.control<string[]>([]),
      vehicles: fb.array<BaseAssignedVehiclesFormGroup>([]),
    });
  }

  public static buildAssignedVehicleForm(
    fb: NonNullableFormBuilder,
    vehicle: SimplifiedFleet,
  ) {
    return fb.group({
      vehicle: fb.control({ value: vehicle, disabled: true }),
      uuid: fb.control({ value: vehicle.uuid, disabled: true }),
    });
  }
}
